'use client'

import Link from 'next/link'
import Image from 'next/image'
import { Github, Instagram, Linkedin, Globe, Facebook } from 'lucide-react'

const socials = [
  { icon: Linkedin, href: 'https://www.linkedin.com/company/apex-visualtech/', label: 'LinkedIn' },
  { icon: Instagram, href: 'https://www.instagram.com/apexvisualtech/', label: 'Instagram' },
  { icon: Facebook, href: 'https://www.facebook.com/people/Apex-VisualTech/61575538461471/', label: 'Facebook' },
  { icon: Globe, href: '#', label: 'Behance' },
]

export default function Footer() {
  return (
    <footer className="relative bg-[#0F172A] border-t border-white/10 overflow-hidden">
      {/* Grain */}
      <div className="grain-overlay" />

      <div className="max-w-[1400px] mx-auto px-6 py-16">
        {/* Top Row */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-12 mb-16">
          {/* Brand */}
          <div className="flex-shrink-0">
            <Link href="/" className="flex items-center group mb-6">
              <Image
                src="/logos/apex-visualtech/logo-transparent-svg.svg"
                alt="Apex Visualtech Logo"
                width={320}
                height={80}
                className="h-16 w-auto object-contain opacity-90 group-hover:opacity-100 transition-opacity"
              />
            </Link>
            <p className="text-[#94A3B8] text-sm max-w-xs leading-relaxed">
              Where Technology Meets Vision. Building premium digital experiences that define brands.
            </p>
          </div>

          {/* Footer Navigation Pillars */}
          <div className="flex flex-wrap gap-12 lg:gap-24">
            {/* Nav Column 1 */}
            <div className="flex flex-col gap-4">
              <h3 className="text-[#F8FAFC] text-sm font-semibold tracking-wider font-sans mb-2">COMPANY</h3>
              <nav className="flex flex-col gap-3">
                <Link href="/" className="text-[#94A3B8] hover:text-[#C8A96E] transition-colors text-sm tracking-[0.06em]">Home</Link>
                <Link href="/about" className="text-[#94A3B8] hover:text-[#C8A96E] transition-colors text-sm tracking-[0.06em]">About Us</Link>
                <Link href="/work" className="text-[#94A3B8] hover:text-[#C8A96E] transition-colors text-sm tracking-[0.06em]">Portfolio</Link>
                <Link href="/blog" className="text-[#94A3B8] hover:text-[#C8A96E] transition-colors text-sm tracking-[0.06em]">Insights</Link>
                <Link href="/contact" className="text-[#94A3B8] hover:text-[#C8A96E] transition-colors text-sm tracking-[0.06em]">Contact</Link>
              </nav>
            </div>

            {/* Nav Column 2: Deep IT Services Links for SEO */}
            <div className="flex flex-col gap-4">
              <h3 className="text-[#F8FAFC] text-sm font-semibold tracking-wider font-sans mb-2">IT &amp; SEO</h3>
              <nav className="flex flex-col gap-3">
                <Link href="/services/it" className="text-[#94A3B8] hover:text-[#C8A96E] transition-colors text-sm tracking-[0.06em]">IT Services Overview</Link>
                <Link href="/services/it#web-dev" className="text-[#94A3B8] hover:text-[#C8A96E] transition-colors text-sm tracking-[0.06em]">Web Development</Link>
                <Link href="/services/it#fullstack" className="text-[#94A3B8] hover:text-[#C8A96E] transition-colors text-sm tracking-[0.06em]">Full Stack Engineering</Link>
                <Link href="/services/it#uiux" className="text-[#94A3B8] hover:text-[#C8A96E] transition-colors text-sm tracking-[0.06em]">UI/UX Design</Link>
                <Link href="/services/it#seo" className="text-[#94A3B8] hover:text-[#C8A96E] transition-colors text-sm tracking-[0.06em]">Technical SEO</Link>
              </nav>
            </div>

            {/* Nav Column 3: Production Links for SEO */}
            <div className="flex flex-col gap-4">
              <h3 className="text-[#F8FAFC] text-sm font-semibold tracking-wider font-sans mb-2">PRODUCTION</h3>
              <nav className="flex flex-col gap-3">
                <Link href="/services/videography" className="text-[#94A3B8] hover:text-[#C8A96E] transition-colors text-sm tracking-[0.06em]">Cinematic Videography</Link>
                <Link href="/services/videography" className="text-[#94A3B8] hover:text-[#C8A96E] transition-colors text-sm tracking-[0.06em]">Drone Cinematography</Link>
                <Link href="/services/graphic-design" className="text-[#94A3B8] hover:text-[#C8A96E] transition-colors text-sm tracking-[0.06em]">Brand Identity Design</Link>
                <Link href="/services/graphic-design" className="text-[#94A3B8] hover:text-[#C8A96E] transition-colors text-sm tracking-[0.06em]">Social Media Graphics</Link>
              </nav>
            </div>
          </div>

          {/* Contact Info — NAP (Name, Address, Phone) — critical for Local SEO */}
          <div className="flex flex-col gap-3">
            {/* Physical address — LocalBusiness SEO signal */}
            <address className="not-italic text-[#94A3B8] text-sm leading-relaxed">
              New Delhi, Delhi NCR,<br />
              India — 110001
            </address>
            <a
              href="mailto:info@apexvisualtech.cloud"
              className="text-[#C8A96E] hover:text-[#D4BA8A] transition-colors text-sm tracking-[0.05em]"
              style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
            >
              info@apexvisualtech.cloud
            </a>
            {/* Socials */}
            <div className="flex gap-4 mt-2">
              {socials.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-9 h-9 border border-white/10 flex items-center justify-center text-[#94A3B8] hover:border-[#C8A96E] hover:text-[#C8A96E] transition-all duration-300"
                >
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Gold rule */}
        <div className="h-px bg-gradient-to-r from-transparent via-[#C8A96E]/30 to-transparent mb-8" />

        {/* Bottom Row — copyright updated to 2026 */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p
            className="text-[#94A3B8] text-xs tracking-[0.1em]"
            style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
          >
            &copy; 2026 APEX VISUALTECH. ALL RIGHTS RESERVED.
          </p>
          <p
            className="text-[#94A3B8] text-xs tracking-[0.1em]"
            style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
          >
            CRAFTED WITH PRECISION IN NEW DELHI, INDIA.
          </p>
        </div>
      </div>
    </footer>
  )
}
